"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validatePersonalInfo = exports.validateLastFourSSN = void 0;
const ValidationResponse_1 = require("./ValidationResponse");
const validationConsts_1 = require("./validationConsts");
const validateName = ({ n, isFirst, }) => {
    const re = validationConsts_1.NAME_REGEX;
    if (!re.test(n.toLowerCase())) {
        return ValidationResponse_1.ValidationResponse.NAME_INVALID;
    }
    if (n.length < validationConsts_1.NAME_MIN_LENGTH) {
        return isFirst
            ? ValidationResponse_1.ValidationResponse.FIRSTNAME_INVALID_SHORT
            : ValidationResponse_1.ValidationResponse.LASTNAME_INVALID_SHORT;
    }
    if (n.length > validationConsts_1.NAME_MAX_LENGTH) {
        return ValidationResponse_1.ValidationResponse.NAME_INVALID_LONG;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
const validatePhoneNumber = (p) => {
    const result = p.match(/\d/g);
    if (result &&
        result[0] !== "0" &&
        result[0] !== "1" &&
        result.join("").length === 10) {
        return ValidationResponse_1.ValidationResponse.VALID;
    }
    return ValidationResponse_1.ValidationResponse.PHONE_NUMBER_INVALID;
};
const hasDomainTypo = (email) => {
    const domain = email.split("@").pop();
    // If domain typed in is up to 2 characters off
    return validationConsts_1.COMMON_PROVIDERS.some((provider) => levenshteinDistance(provider, domain) >= 1 &&
        levenshteinDistance(provider, domain) <= 2);
};
const levenshteinDistance = (s1, s2) => {
    const dp = Array.from({ length: s1.length + 1 }, () => Array(s2.length + 1).fill(0));
    for (let i = 0; i <= s1.length; i++) {
        for (let j = 0; j <= s2.length; j++) {
            if (i === 0) {
                dp[0][j] = j;
            }
            else if (j === 0) {
                dp[i][0] = i;
            }
            else {
                dp[i][j] = Math.min(dp[i - 1][j - 1] + (s1[i - 1] === s2[j - 1] ? 0 : 1), dp[i - 1][j] + 1, dp[i][j - 1] + 1);
            }
        }
    }
    return dp[s1.length][s2.length];
};
/** Validates an email address
 *
 * @param {string} e - The email address to be validated.
 * @returns {ValidationResponse} - A validation response indicating the result of the validation.
 *    - email is valid -> ValidationResponse.VALID
 *    - email fails regex -> ValidationResponse.EMAIL_INVALID
 *    - email is too long -> ValidationResponse.EMAIL_INVALID_LONG
 **/
const validateEmail = (e, currEmail) => {
    if (currEmail && e === currEmail) {
        return ValidationResponse_1.ValidationResponse.EMAIL_INVALID_SAME;
    }
    if (e.length > validationConsts_1.EMAIL_MAX_LENGTH) {
        return ValidationResponse_1.ValidationResponse.EMAIL_INVALID_LONG;
    }
    if (!validationConsts_1.EMAIL_REGEX.test(e.toLowerCase())) {
        return ValidationResponse_1.ValidationResponse.EMAIL_INVALID;
    }
    if (hasDomainTypo(e)) {
        return ValidationResponse_1.ValidationResponse.EMAIL_WARNING_TYPO;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/** Validates a date
 *
 *  @param {string} d - The date to be validated. (MM/DD/YYYY)
 * @returns {ValidationResponse} - A validation response indicating the result of the validation.
 */
const validateDate = (d) => {
    if (d.length !== 10) {
        return ValidationResponse_1.ValidationResponse.DATE_INVALID;
    }
    // check if date is valid format
    const dateObj = new Date(d);
    if (Number.isNaN(dateObj.getDate())) {
        return ValidationResponse_1.ValidationResponse.DATE_INVALID;
    }
    const currentDate = new Date();
    if (dateObj.getFullYear() < 1900 ||
        dateObj.getFullYear() > currentDate.getFullYear()) {
        return ValidationResponse_1.ValidationResponse.DATE_INVALID;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
/** Validates a birth date
 *
 *  @param {string} d - The birth date to be validated. (MM/DD/YYYY)
 *  @returns {ValidationResponse} - A validation response indicating the result of the validation.
 */
const validateBirthDate = (d, isAuthUser) => {
    const dateValidation = validateDate(d);
    if (dateValidation !== ValidationResponse_1.ValidationResponse.VALID) {
        return dateValidation;
    }
    let minAge = isAuthUser ? validationConsts_1.AUTH_MIN_AGE : validationConsts_1.PRIMARY_MIN_AGE;
    const currentDate = new Date();
    const dateObj = new Date(d);
    const diff = currentDate.getTime() - dateObj.getTime();
    const minAgeMS = minAge * 365 * 24 * 60 * 60 * 1000;
    if (diff < minAgeMS) {
        return isAuthUser
            ? ValidationResponse_1.ValidationResponse.AGE_INVALID_AUTH
            : ValidationResponse_1.ValidationResponse.AGE_INVALID;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
const validateFullSsn = (ssn, last4) => {
    if (ssn.length < 11) {
        return ValidationResponse_1.ValidationResponse.FULL_SSN_INVALID;
    }
    // can't have 9 as first digit
    // can't have 666, 000 as first 3 digits
    // can't have 00 as middle 2 digits
    // can't have 0000 as last 4 digits
    // correct length/format
    const re = validationConsts_1.SSN_REGEX;
    if (!re.test(ssn)) {
        return ValidationResponse_1.ValidationResponse.FULL_SSN_INVALID;
    }
    if (last4 && ssn.slice(-4) !== last4) {
        return ValidationResponse_1.ValidationResponse.FULL_SSN_INVALID_NOMATCH;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
const validateLastFourSSN = (ssn) => {
    if (ssn.length < 4) {
        return ValidationResponse_1.ValidationResponse.LAST_FOUR_SSN_SHORT;
    }
    // cannot be 0000, must be 4 digits
    const re = /^(?!0000)[0-9]{4}$/;
    if (!re.test(ssn)) {
        return ValidationResponse_1.ValidationResponse.FULL_SSN_INVALID;
    }
    return ValidationResponse_1.ValidationResponse.VALID;
};
exports.validateLastFourSSN = validateLastFourSSN;
exports.validatePersonalInfo = {
    name: validateName,
    phoneNumber: validatePhoneNumber,
    email: validateEmail,
    date: validateDate,
    birthDate: validateBirthDate,
    fullSsn: validateFullSsn,
    lastFourSsn: exports.validateLastFourSSN,
};
